import { WorkEmpty } from '../../components/work-page.tsx';
import { WorkPage, WorkSection } from '../../components/work-page.tsx';
import '../portal-page.css';
import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { partnerApi, PartnerApiError } from '../../api/partner.ts';
import { partnerMayAct, usePartnerIdentity } from '../../components/partner-layout.tsx';
import { Alert, Button, LoadingBlock, TextArea, TextInput } from '../../components/ui.tsx';
import { useDocumentTitle } from '../../hooks/index.ts';
import { Pill, shortDate, TableShell } from './common.tsx';

function toneFor(status: string): 'good' | 'warn' | 'bad' | 'neutral' {
  if (status === 'approved' || status === 'auto_approved') return 'good';
  if (status === 'rejected') return 'bad';
  if (status === 'conflict' || status === 'pending') return 'warn';
  return 'neutral';
}

/**
 * "This customer was mine" — the manual route for a signup that missed the
 * link.
 *
 * A claim is a request, not a change: the Worker decides whether it can be
 * auto-approved and otherwise leaves it for a human, and it returns the reason
 * either way so a rejection is explainable rather than mysterious.
 */
export default function PartnerClaimsPage() {
  useDocumentTitle('Attribution claims');
  const identity = usePartnerIdentity();
  const queryClient = useQueryClient();

  const [customerEmail, setCustomerEmail] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [approximateSignupDate, setApproximateSignupDate] = useState('');
  const [explanation, setExplanation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<string | null>(null);

  const requests = useQuery({
    queryKey: ['partner', 'attribution-requests'],
    queryFn: partnerApi.attributionRequests,
  });

  const create = useMutation({
    mutationFn: () =>
      partnerApi.createAttributionRequest({
        customerEmail,
        ...(businessName ? { businessName } : {}),
        ...(approximateSignupDate ? { approximateSignupDate } : {}),
        explanation,
      }),
    onSuccess: (result) => {
      setError(null);
      setSubmitted(result.status);
      setCustomerEmail('');
      setBusinessName('');
      setApproximateSignupDate('');
      setExplanation('');
      void queryClient.invalidateQueries({ queryKey: ['partner', 'attribution-requests'] });
    },
    onError: (caught) => {
      setSubmitted(null);
      setError(caught instanceof PartnerApiError ? caught.message : 'That claim could not be submitted.');
    },
  });

  // A suspended or terminated partner keeps their figures and loses the write
  // routes — the same rule `requireActivePartner` applies on the server, read
  // from the same shared set so `payout_hold` and `compliance_review`, which
  // the API still accepts, are not refused here.
  const canSubmit = partnerMayAct(identity.data?.partner.status);

  function submit(event: FormEvent) {
    event.preventDefault();
    create.mutate();
  }

  return (
    <div className="portal-page">
      <WorkPage
        title="Attribution claims"
        description="Ask for a customer to be attributed to you when they signed up without using your link."
      >
        <WorkSection title="Claim a customer">
          {submitted ? (
            <div className="mt-3">
              <Alert tone="success" title="Claim submitted">
                {submitted === 'auto_approved'
                  ? 'It matched our records and has been approved. The customer now appears under Customers.'
                  : 'It has been recorded and will be reviewed. You can follow it in the table below.'}
              </Alert>
            </div>
          ) : null}
          {error ? (
            <div className="mt-3">
              <Alert tone="error">{error}</Alert>
            </div>
          ) : null}
          {!canSubmit ? (
            <div className="mt-3">
              <Alert tone="warning">New claims are paused while your partner account is not active.</Alert>
            </div>
          ) : null}
          <form className="mt-3" onSubmit={submit} noValidate>
            <TextInput
              label="Customer email"
              type="email"
              required
              value={customerEmail}
              onChange={(event) => setCustomerEmail(event.target.value)}
              hint="The address they signed up with."
            />
            <TextInput
              label="Business name"
              value={businessName}
              onChange={(event) => setBusinessName(event.target.value)}
              hint="Optional, but it helps a reviewer match the account."
            />
            <TextInput
              label="Approximate signup date"
              type="date"
              value={approximateSignupDate}
              onChange={(event) => setApproximateSignupDate(event.target.value)}
            />
            <TextArea
              label="How you referred them"
              required
              rows={4}
              value={explanation}
              onChange={(event) => setExplanation(event.target.value)}
            />
            <Button type="submit" variant="primary" busy={create.isPending} disabled={!canSubmit}>
              Submit claim
            </Button>
          </form>
        </WorkSection>

        {requests.isLoading ? <LoadingBlock label="Loading your claims…" /> : null}
        {requests.isError ? (
          <Alert tone="error" title="Your claims could not be loaded">
            {requests.error instanceof Error ? requests.error.message : 'Please try again in a moment.'}
          </Alert>
        ) : null}
        {requests.data && requests.data.requests.length === 0 ? (
          <WorkEmpty title="No claims yet">
            Most customers are attributed automatically by your referral link; this is for the ones that were not.
          </WorkEmpty>
        ) : null}
        {requests.data && requests.data.requests.length > 0 ? (
          <TableShell
            label="Attribution claims"
            head={
              <tr>
                <th scope="col" className="px-4 py-3 font-semibold">
                  Customer
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  Status
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  Submitted
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  Decided
                </th>
              </tr>
            }
          >
            {requests.data.requests.map((request) => (
              <tr key={request.id} className="border-b border-[color:var(--color-line)] last:border-b-0">
                <td className="px-4 py-3">
                  <span className="font-medium">{request.customerEmail}</span>
                  {request.businessName ? (
                    <div className="text-xs text-[color:var(--color-muted)]">{request.businessName}</div>
                  ) : null}
                </td>
                <td className="px-4 py-3">
                  <Pill value={request.status} tone={toneFor(request.status)} />
                  {/* A decision reason runs to 500 characters. The shell sizes
                    to its content, so an unbounded one would set the width of
                    every other row; bounded, it wraps in its own column. */}
                  {request.decisionReason ? (
                    <div className="table-text mt-1 max-w-64 text-xs text-[color:var(--color-muted)]">
                      {request.decisionReason}
                    </div>
                  ) : null}
                </td>
                <td className="px-4 py-3">{shortDate(request.createdAt)}</td>
                <td className="px-4 py-3">{shortDate(request.decidedAt)}</td>
              </tr>
            ))}
          </TableShell>
        ) : null}
      </WorkPage>
    </div>
  );
}
